// The title screen (docs/18 mockup "Main menu"): the night clearing — stars, the tree line, a
// campfire with the fellowship round it — with the wordmark in the sky, the main menu on the left
// (Continue when a World is remembered — Rejoin while my party is in a fight, docs/54 — Join a friend / New fellowship → Play online (docs/17),
// Heroes & Ledger → the World, Collection → the collection log with its New badge (docs/131), Settings, Credits), friends online on the right with Join, and the
// versions and key hints in the foot strip. Arrow keys move through the menu, Enter selects.
import { useEffect, useRef, type KeyboardEvent } from 'react';
import { Button, Kbd, MenuItem, MenuList, Modal, Panel } from '../components';
import { useRoute } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useFriends, useMyProfile, useParties, useWorld, useWorldClock, useWorldPartyMembers } from '../../core/subscriptions/hooks';
import { shortDate } from '../../core/story/calendar';
import { worldTitle } from '../../core/story/worldName';
import { identityHex, shortId } from '../../core/net/connection';
import { useOverlay } from '../overlay';
import { NightClearing } from './NightClearing';
import { playSfx } from '../../core/audio';
import { usePlace } from '../../core/audio/places';
import { FriendLine, requestCreateFocus, useClearingHeroes, useEnterWorld, useNow } from './PlayOnline';
import { useCollectionHasNew } from './Collection';

const SHOWN_FRIENDS = 5;
let titleSounded = false;

function FriendsOnline() {
  const friends = useFriends();
  const profile = useMyProfile();
  const go = useRoute((s) => s.go);
  const now = useNow();
  const accepted = friends.filter((f) => f.state === 'friend');
  const asking = friends.filter((f) => f.state === 'incoming').length;
  const online = accepted.filter((f) => f.online).length;
  return (
    <Panel title="Friends online" label="Friends online" className="fk-title__friends" data-testid="title-friends" actions={accepted.length > 0 ? <span data-testid="title-friends-count">{online} of {accepted.length}</span> : undefined}>
      {accepted.length === 0
        ? (
          <div className="fk-title__nofriends">
            <p>{profile ? 'No friends yet. Add someone by their username and their lobbies show up here.' : 'Claim a username, then add friends by theirs.'}</p>
            <Button small onClick={() => go('online')} data-testid="title-find-friends">Find friends</Button>
          </div>
        )
        : <ul className="fk-online__friend-list fk-title__friend-list">{accepted.slice(0, SHOWN_FRIENDS).map((f, i) => <FriendLine key={f.id.toString()} f={f} index={i} now={now} testId="title-friend" />)}</ul>}
      {(asking > 0 || accepted.length > SHOWN_FRIENDS) && (
        <button type="button" className="fk-title__more" onClick={() => go('online')} data-testid="title-friend-requests">
          {asking > 0 ? `${asking} friend request${asking === 1 ? '' : 's'} waiting` : `${accepted.length - SHOWN_FRIENDS} more`} ›
        </button>
      )}
    </Panel>
  );
}

/** Up/Down (and Home/End) move focus through the menu rows; Enter/Space press them natively. */
function onMenuKey(e: KeyboardEvent<HTMLElement>) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
  const items = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')];
  if (!items.length) return;
  e.preventDefault();
  const at = items.indexOf(document.activeElement as HTMLButtonElement);
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
  items[next]!.focus();
}

export function TitleScreen() {
  usePlace({ key: 'place.title' });   // docs/44: a quiet night outside the village
  const go = useRoute((s) => s.go);
  const worldId = useSession((s) => s.worldId);
  const identity = useSession((s) => s.identity ?? '');
  const compat = useSession((s) => s.compat);
  const world = useWorld(worldId);
  const clock = useWorldClock(worldId);
  const profile = useMyProfile();
  const heroes = useClearingHeroes(world ? worldId : undefined);
  const enter = useEnterWorld();
  const openSettings = useOverlay((s) => s.openSettings);
  const credits = useOverlay((s) => s.credits);
  const openCredits = useOverlay((s) => s.openCredits);
  const closeCredits = useOverlay((s) => s.closeCredits);
  // docs/131 CL8: the Collection button wears New while a collected piece is unseen (no count here)
  const collectionNew = useCollectionHasNew();
  const menuRef = useRef<HTMLElement>(null);
  const hasWorld = !!world;
  // docs/54 RC7: a party of mine is mid-fight or on the road — Continue says it rejoins (the route
  // driver takes it straight back to the board or the map)
  const parties = useParties(world ? worldId : undefined);
  const members = useWorldPartyMembers(world ? worldId : undefined);
  const myParty = parties.find((p) => p.state !== 2 && members.some((m) => m.partyId === p.id && identityHex(m.owner) === identity));
  const rejoin = myParty?.encounterId !== undefined && myParty?.encounterId !== null ? 'Your party is in a fight' : undefined;
  const online = () => go('online');
  const fellowship = () => { requestCreateFocus(); go('online'); };
  // Focus the lead row once so Enter / the arrows work straight away (no scroll, no ring until keys are used).
  useEffect(() => { menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true }); }, [hasWorld]);
  // docs/34: the game has loaded — its stinger, once a session (held for the first gesture where the browser insists)
  useEffect(() => { if (titleSounded) return; titleSounded = true; playSfx('ui.title', { whenUnlocked: true }); }, []);
  let i = 0;
  return (
    <main className="fk-title fk-hit" data-testid="title-screen">
      <NightClearing heroes={heroes} />
      <header className="fk-title__mark">
        <span className="fk-title__eyebrow">A fellowship, kept</span>
        <h1 className="fk-title__word">Fellowkeep</h1>
        <p className="fk-title__tag"><span>Four heroes. One turn. Every number exact.</span></p>
      </header>
      <nav className="fk-title__menu fk-panel" aria-label="Main menu" ref={menuRef} onKeyDown={onMenuKey}>
        <MenuList label="Main menu">
          {world && worldId !== undefined && (
            <MenuItem index={i++} primary className="fk-title__item fk-title__item--lead" onClick={() => enter(worldId)} data-testid="title-continue">
              <span className="fk-title__item-name">{rejoin ? 'Rejoin' : 'Continue'}</span>
              <span className="fk-title__item-sub">{worldTitle(world)}{rejoin ? ` · ${rejoin}` : clock && ` · ${shortDate(clock)}`}</span>
            </MenuItem>
          )}
          <MenuItem index={i++} primary={!world} className={`fk-title__item ${world ? '' : 'fk-title__item--lead'}`} onClick={online} data-testid="title-play">
            <span className="fk-title__item-name">Join a friend</span>
            {!world && <span className="fk-title__item-sub">Friends, lobbies and open Worlds</span>}
          </MenuItem>
          <MenuItem index={i++} className="fk-title__item" onClick={fellowship} data-testid="title-new">New fellowship</MenuItem>
          <MenuItem index={i++} className="fk-title__item" disabled={!world} title={world ? undefined : 'Join or found a World first'} onClick={() => worldId !== undefined && enter(worldId)} data-testid="title-heroes">Heroes &amp; Ledger</MenuItem>
          <MenuItem index={i++} className="fk-title__item" onClick={() => go('collection')} data-testid="title-collection">
            <span className="fk-title__item-name">Collection{collectionNew && <span className="fk-title__new" data-testid="title-collection-new">New</span>}</span>
          </MenuItem>
          <MenuItem index={i++} className="fk-title__item" onClick={openSettings} data-testid="title-settings">Settings</MenuItem>
          <MenuItem index={i++} className="fk-title__item" onClick={openCredits} data-testid="title-credits">Credits</MenuItem>
        </MenuList>
      </nav>
      <FriendsOnline />
      <footer className="fk-title__foot">
        <span title={identity}>client {__FK_CLIENT_VERSION__}{compat ? ` · ruleset ${compat.rulesetVersion}` : ''} · {identity ? <>signed in as {profile ? `@${profile.username}` : shortId(identity)}</> : 'signing in…'}</span>
        <span className="fk-title__keys"><Kbd>Enter</Kbd> Select <Kbd>Esc</Kbd> Back</span>
      </footer>
      <Modal open={credits} onClose={closeCredits} eyebrow="Fellowkeep" title="Credits" size="narrow" testId="credits" footer={<Button primary onClick={closeCredits}>Close</Button>}>
        <p style={{ margin: 0 }}>Art: <strong>Oryx Design Lab</strong>, 16-Bit Fantasy Sprite Set.</p>
        <p style={{ margin: 0 }}>Sound effects: <strong>Cyberwave Orchestra</strong>, fantasy sound effects library.</p>
        <p style={{ margin: 0 }}>Sound effects and music: <strong>SYNERGY Sounds</strong>.</p>
        <p style={{ margin: 0 }}>Announcer: <strong>WARLORD</strong> by <strong>VoiceBosch</strong>, SoundBiter SFX library, under <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noreferrer">CC BY-SA 4.0</a>.</p>
        <p style={{ margin: 0 }}>Type: <strong>Cinzel</strong> by Natanael Gama and <strong>Alegreya Sans</strong> by Huerta Tipográfica, both under the SIL Open Font License.</p>
        <p style={{ margin: 0 }}>Engine: SpacetimeDB, Phaser, React. The simulator is a Rust crate compiled to wasm so the preview and the server never disagree.</p>
      </Modal>
    </main>
  );
}
